import { CustomRulesView } from '../view/CustomRulesView';
import { templateCustomRules } from '../view/template';
import { CustomRulesRepository } from '../repository/CustomRulesRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { CustomRulesWebComponent } from '../index';
import {
  Actions,
  CustomRulesEvents,
  CustomRulesItem,
  CustomRulesWebComponentSchema,
  DetectionRuleType,
  GridType,
  operators,
  ruleFields,
  RuleType,
  Severity,
  Status,
  Targets,
  Type,
} from '../types';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { GridEvents } from '../../../grid/types/common';
import { CustomRulesSidePanelWebComponent } from '../../customRulesSidePanel';
import { CustomRulesSidePanelEvents } from '../../customRulesSidePanel/types';
import { translateFn } from '../../../utils/translator.helper';
import { GzDialog, GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import {
  GzAddRuleWebComponent,
  WizardMaxStepWithAutomaticActions,
  WizardMaxStepWithoutAutomaticActions,
  WizardMinStep,
} from '../../addRule';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { ButtonTypes } from '../../../ui/gzButton/types';
import {
  START_YARA_SCAN,
  SWITCH_TO_ADVANCED,
  SWITCH_TO_BASIC,
  TARGET_CHANGE,
  WIZARD_STEP_CHANGE,
  YARA_VALIDITY_CHANGED,
  YARA_VALUE_CHANGED,
} from '../../addRule/constants';
import { GzYaraDefinitionWebComponent } from '../../addRule/component/yaraDefinition';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import {
  GzToast,
  ToastActionButtonMode,
  ToastPositionX,
  ToastPositionY,
  ToastSeverity,
} from '../../../ui/gzToastNotification/type';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { GzRuleDefinitionWebComponent } from '../../addRule/component/ruleDefinition';
import { GzChipsGeneratorInputWebComponent } from '../../../ui/gzChipsGeneratorInput';
import { RadioEvent } from '../../../ui/gzRadio/types';
import { InfoBoxSeverity } from '../../../ui/gzInfoBox/types';
import { cloneDeep } from 'lodash';
import { Feature, Rights } from '@nirvana/shared/constants';
import { checkRuleCriteriaTypes, convertRuleFromFrontendToBackendFormat } from '../utils';
import { ServerResponseKind } from '../../../types/common/types';
import { GzRuleConfigurationWebComponent } from '../../addRule/component/ruleSettings/component/GzRuleConfigurationWebComponent';

export class CustomRulesController extends BaseWebComponentController {
  public component: CustomRulesWebComponent;
  public dataRepository: CustomRulesRepository;
  public view: CustomRulesView;
  public viewConstructor: typeof CustomRulesView;
  public dataRepositoryConstructor: typeof CustomRulesRepository;

  private selectedItems: Array<CustomRulesItem> = [];

  private addRuleDialog: GzDialogWebComponent;

  private startScanDialog: GzDialogWebComponent;

  private SPECIFIC_FOLDER = '1';
  private ALL_DISKS = '2';

  constructor(component: CustomRulesWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: CustomRulesView,
      viewTemplate: templateCustomRules,
      dataRepositoryConstructor: CustomRulesRepository,
    });
  }

  async getDataForView(): Promise<CustomRulesWebComponentSchema> {
    return {
      data: await this.dataRepository.getDataForView(),
      externalFilters: this.component.externalFilters,
    };
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$addRuleButton,
        event: 'click',
        callback: this.addRuleListener,
      },
      {
        element: this.view.dom.$addEmptyStateButton,
        event: 'click',
        callback: this.addRuleListener,
      },
      {
        element: this.view.dom.$changeStatusSelect,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.changeStatusListener.bind(this),
      },
      {
        element: this.view.dom.$deleteButton,
        event: 'click',
        callback: this.deleteButtonListener.bind(this),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChange.bind(this),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.onRowClick.bind(this),
      },
      {
        element: this.view.dom.$grid,
        event: CustomRulesEvents.EDIT,
        callback: this.editRuleListener.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: CustomRulesSidePanelEvents.DELETE,
        callback: this.sidePanelDelete.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: CustomRulesSidePanelEvents.EDIT,
        callback: this.editRuleListener.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: CustomRulesSidePanelEvents.SCAN,
        callback: this.sidePanelScan.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: CustomRulesSidePanelEvents.GET_PREV_RULE,
        callback: this.sidePanelPrevRule.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: CustomRulesSidePanelEvents.GET_NEXT_RULE,
        callback: this.sidePanelNextRule.bind(this),
      },
      {
        element: this.view.dom.$wrapper,
        event: START_YARA_SCAN,
        callback: this.handleStartYaraScan.bind(this),
      },
      {
        element: this.view.dom.$wrapper,
        event: GzDialogEvents.CANCEL,
        callback: this.handleWizardClose.bind(this),
      },
    ];
  }

  private handleWizardClose(event: WebComponentEvent) {
    GzToastNotificationWebComponent.closeAllToasts();
  }

  private sidePanelDelete(event: WebComponentEvent) {
    this.view.deleteRule([event.detail.rule]);
  }

  private buildDialogBodyInnerHTML() {
    return `
      <div class='pl-5'>
        <p class='pb-3'>${translateFn('You can define which local folders this rule will scan.')}</p>
        <gz-radio-group gz-form-input id='pathsGroup' selectedOption="1" >
          <gz-radio class='' id='specificPaths' value="${this.SPECIFIC_FOLDER}" label="${translateFn(
      'Specific folders',
    )}"></gz-radio>
          <gz-chips-generator-input
            class="pl-3 pt-1 mr-1"
            id="pathsComponentId"
            hideCopyToClipboardButton
            .strings="${{ placeholder: translateFn('Type here') }}"
          ></gz-chips-generator-input>
          <gz-radio class='mt-3' id='allDisk' value="${this.ALL_DISKS}" label="${translateFn(
      'All disk drives',
    )}"></gz-radio>
        </gz-radio-group>
      </div>
      <gz-info-box severity="${InfoBoxSeverity.WARNING}" class='pl-5 pt-2'>
        ${translateFn('Scanning all disk drives is resource intensive and may cause performance issues.')}
      </gz-info-box>`;
  }

  protected async sidePanelScan(event) {
    this.createScanDialog(event);
  }

  private createScanDialog(event) {
    const dialogBody = document.createElement('div');
    dialogBody.classList.add('pt-3');
    dialogBody.innerHTML = this.buildDialogBodyInnerHTML();

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
        <gz-button id="startScanButton" type="outline" class="mr-2" disabled>${translateFn('Scan')}</gz-button>
        <span class="w-full d-flex justify-end">
            <gz-button id="cancelButton" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
        </span>
    `;

    const dialogConfigs: GzDialog = {
      headline: translateFn('Define local targets'),
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
      isWindow: true,
    };

    const parent = document.querySelector('gz-custom-rules')?.shadowRoot?.querySelector('.wrapper') as HTMLElement;

    this.startScanDialog = GzDialogWebComponent.buildDialogElement(dialogConfigs, parent);

    this.startScanDialog.updateComplete.then(() => {
      const pathsChips = this.startScanDialog.shadowRoot?.querySelector(
        '#pathsComponentId',
      ) as GzChipsGeneratorInputWebComponent;

      const cancelButton = this.startScanDialog.shadowRoot?.querySelector('#cancelButton');
      cancelButton?.addEventListener('click', () => {
        this.startScanDialog.dismiss();
      });

      const startScanButton = this.startScanDialog.shadowRoot?.querySelector(
        '#startScanButton',
      ) as GzButtonWebComponent;

      startScanButton?.addEventListener('click', async () => {
        await this.startScan(event.detail.rule, pathsChips.getFormValue());
      });

      this.startScanDialog.addEventListener(RadioEvent.SELECTION_CHANGED, (selectionEvent: WebComponentEvent) => {
        if (selectionEvent.detail.selectedValue === this.ALL_DISKS) {
          startScanButton.disabled = false;
        } else {
          const currentPathsChips = pathsChips.getFormValue();
          startScanButton.disabled = currentPathsChips.length <= 0;
        }
      });

      pathsChips.addEventListener('change', (changeEvent: WebComponentEvent) => {
        const currentPathsChips = changeEvent.detail.value;
        startScanButton.disabled = currentPathsChips.length <= 0;
      });
    });
  }

  private async sidePanelPrevRule(event: WebComponentEvent) {
    const index = event.detail.rowIndex - 1;
    const maxRowIndex = event.detail.maxRowIndex;
    const row = this.view.dom.$grid.controller.getRowByIndex(index);
    if (!row) {
      return;
    }

    this.scrollUp(index);
    await this.generateSidePanel(row, index, maxRowIndex);
  }

  private async sidePanelNextRule(event: WebComponentEvent) {
    const index = event.detail.rowIndex + 1;
    const maxRowIndex = event.detail.maxRowIndex;
    const row = this.view.dom.$grid.controller.getRowByIndex(index);
    if (!row) {
      return;
    }

    this.scrollDown(index);
    await this.generateSidePanel(row, index, maxRowIndex);
  }

  private async editRuleListener(event: WebComponentEvent) {
    const sidePanel = this.view.dom.$sideMenu.querySelector('custom-rules-side-panel');

    if (sidePanel && sidePanel.editButton) {
      sidePanel.editButton.showSpinner = true;
      sidePanel.editButton.disabled = true;
    }

    await this.generateAddRule(event.detail.rule);

    if (sidePanel && sidePanel.editButton) {
      sidePanel.editButton.showSpinner = false;
      sidePanel.editButton.disabled = false;
    }
  }

  private convertRule(rule) {
    const result = {
      ...rule,
      targets: {
        ...rule.targets,
      },
    };

    if (rule.targets.companiesIds) {
      result.targets.type = Targets.COMPANIES;
    } else if (rule.targets.endpointTags) {
      result.targets.type = Targets.ENDPOINT;
    } else {
      result.targets.type = Targets.COMPANY;
    }

    if (rule.type === RuleType.YARA) {
      result.query = cloneDeep(rule.query);
      return result;
    }

    const query: any = {
      type: rule.query.type,
      detection: rule.query.detection,
    };

    const rulesCriteria: any = [];
    for (const r of rule.query.rules) {
      const ruleCriteria: any = {};
      ruleCriteria.criteriaValue = Object.keys(ruleFields[query.type]).find(key => ruleFields[query.type][key] === r.field);
      ruleCriteria.operatorValue = Number(Object.keys(operators).find(key => operators[key] === r.relation));
      ruleCriteria.inputValue = r.value;
      rulesCriteria.push(ruleCriteria);
    }

    query.rules = rulesCriteria;
    result.query = query;

    if (!rule.tags) {
      result.tags = [];
    }

    if (GridType.DETECTION === this.dependencies.gridType && rule.automaticActions) {
      result.automaticActions = cloneDeep(rule.automaticActions);
    }

    return result;
  }

  private async generateAddRule(rule: any = null) {
    const dialogBody = document.createElement('div');
    dialogBody.style.cssText = 'display:flex;align-items:center;height:100%;width:100%';
    dialogBody.innerHTML = `<gz-progress-spinner style="justify-content:center;display:flex;" indeterminate="" spinnerwidth="large" ></gz-progress-spinner>`;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = '';

    const dialogConfigs: GzDialog = {
      headline: rule ? translateFn('Edit rule') : translateFn('Add rule'),
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
      isWindow: true,
    };

    this.addRuleDialog = GzDialogWebComponent.buildDialogElement(dialogConfigs, this.view.dom.$wrapper);
    const ruleCompanyId = rule ? rule.companyId : null;
    const availableCompanies = await this.dependencies.api.clients.CustomRules.getAvailableCompanies(ruleCompanyId).toPromise();

    const ruleParam = rule
                      ? this.convertRule(rule)
                      : {
        companyId: this.dependencies.getUserCompany()['companyId'],
        availableCompanies: [
          {
            _id: this.dependencies.getUserCompany()['companyId'],
            name: this.dependencies.getUserCompany()['companyName'],
          },
        ],
        name: '',
        details: '',
        tags: [],
        status: Status.ENABLED,
        type: RuleType.BASIC,
        severity: Severity.HIGH,
        generateRca: true,
        targets: {
          type: Targets.COMPANY,
          companyId: this.dependencies.getUserCompany()['companyId'],
        },
        query: {
          type: DetectionRuleType.PROCESS,
          rules: [
            {
              criteriaValue: null,
              operatorValue: null,
              inputValue: null,
            },
          ],
        },
      };

    if (!rule && this.dependencies.getCurrentUser()['mdrSuperAnalyst']
      && this.dependencies.getUserCompany()['companyId'] !== this.dependencies.getActiveCompany()['companyId']
    ) {
      ruleParam.targets = {
        type: Targets.COMPANIES,
        companiesIds: [this.dependencies.getActiveCompany()['companyId']]
      };
    }

    const company = rule && rule.availableCompanies ? rule.availableCompanies.filter(availableCompany => availableCompany._id === rule.companyId) : [];
    const addRuleComponent = new GzAddRuleWebComponent();
    addRuleComponent.wizardStep = 1;
    addRuleComponent.gridType = this.dependencies.gridType;
    addRuleComponent.company = company.length > 0
                               ? {
        companyId: company[0]._id,
        companyName: company[0].name,
      }
      : this.dependencies.getUserCompany();
    addRuleComponent.availableCompanies = availableCompanies.data;
    addRuleComponent.availableCompaniesWithEdr = availableCompanies.edrCompanies;
    addRuleComponent.isPartnerCompany = availableCompanies.isPartnerCompany;
    addRuleComponent.isGZ = this.dependencies.isGZ();
    const response = await this.dependencies.api.clients.CustomRules.getTagsSuggestions([]).toPromise();
    addRuleComponent.tags = response.data;
    addRuleComponent.rule = ruleParam;
    addRuleComponent.oldRule = rule ? this.convertRule(rule) : null;
    addRuleComponent.hasAccessOnCompany = await this.dependencies.api.clients.CustomRules.hasAccessOnCompany(
      addRuleComponent.company.companyId,
    ).toPromise();
    addRuleComponent.hasEditInfoStep3 = this.hasUnavailableTags(rule);
    addRuleComponent.isEdrStandalone = this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_COMPANION_USABLE);
    addRuleComponent.platformHasFeature = this.dependencies.platformHasFeature;
    addRuleComponent.userCompany = this.dependencies.getUserCompany();

    try {
      const companyId = rule ? rule.companyId : null;
      const response2 = await this.dependencies.api.clients.CustomRules.getAvailableCompaniesTags(
        companyId
      ).toPromise();
      addRuleComponent.availableEndpointTags = response2.data;
    } catch (e) {
      throw new Error(e);
    }

    dialogBody.innerHTML = '';
    dialogBody.append(addRuleComponent);

    dialogFooter.innerHTML = `
        <gz-button id="next" type="outline" class="mr-2">${translateFn('Next')}</gz-button>
        <gz-button id="back" type="quiet" theme="light" color="secondary" class="mr-2" disabled>${translateFn(
      'Back',
    )}</gz-button>
        <span class="w-full d-flex justify-end">
            <gz-button id="cancel" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
        </span>
    `;

    this.view.dom.$addRuleButton.showSpinner = false;
    this.view.dom.$addRuleButton.disabled = false;
    this.view.dom.$addEmptyStateButton.showSpinner = false;
    this.view.dom.$addEmptyStateButton.disabled = false;

    const footerCancelBtn = dialogFooter.querySelector('#cancel') as GzButtonWebComponent;
    const footerNextBtn = dialogFooter.querySelector('#next') as GzButtonWebComponent;
    const footerBackBtn = dialogFooter.querySelector('#back') as GzButtonWebComponent;

    addRuleComponent.addEventListener(SWITCH_TO_ADVANCED, () => {
      footerNextBtn.textContent = translateFn('Check rule');
      footerNextBtn.disabled = true;
    });
    addRuleComponent.addEventListener(YARA_VALIDITY_CHANGED, (event: CustomEvent) => {
      footerNextBtn.textContent = event.detail.valid ? translateFn('Next') : translateFn('Check rule');
    });
    addRuleComponent.addEventListener(SWITCH_TO_BASIC, () => {
      footerNextBtn.textContent = translateFn('Next');
      footerNextBtn.disabled = false;
    });
    addRuleComponent.addEventListener(YARA_VALUE_CHANGED, (event: CustomEvent) => {
      footerNextBtn.disabled = !event.detail.input.trim() || event.detail.hasError;
    });

    addRuleComponent.addEventListener(TARGET_CHANGE, (event: CustomEvent) => {
      footerNextBtn.disabled = event.detail.targets.type === undefined;
    });

    footerCancelBtn?.addEventListener('click', () => {
      this.addRuleDialog.dismiss();
    });

    footerNextBtn?.addEventListener('click', async () => {
      await this.addRuleClickOnNext(footerNextBtn, footerBackBtn, addRuleComponent);
    });

    footerBackBtn?.addEventListener('click', () => {
      this.addRuleClickOnBack(footerNextBtn, footerBackBtn, addRuleComponent);
    });

    addRuleComponent.addEventListener(WIZARD_STEP_CHANGE, () => {
      if (addRuleComponent.wizardStep === WizardMinStep) {
        footerBackBtn.disabled = true;
      }
      if (addRuleComponent.wizardStep !== this.getWizardMaxStep(addRuleComponent.rule)) {
        footerNextBtn.innerText = translateFn('Next');
        footerNextBtn.type = ButtonTypes.OUTLINE;
      }
    });
  }

  private getWizardMaxStep(rule: any): number {
    return GridType.DETECTION === this.dependencies.gridType &&
           RuleType.BASIC === rule.type &&
           this.dependencies.platformHasFeature(Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTIONS) &&
           !this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_COMPANION_USABLE)
           ? WizardMaxStepWithAutomaticActions
           : WizardMaxStepWithoutAutomaticActions;
  }

  private hasUnavailableTags(rule) {
    if (!rule || !rule.targets.endpointTags) {
      return false;
    }

    const availableCompaniesIds = rule.availableCompanies.map(company => company._id);
    const unavailableTags = rule.targets.endpointTags.filter(tag => !availableCompaniesIds.includes(tag.companyId));

    return unavailableTags.length !== 0;
  }

  private async addRuleClickOnNext(footerNextBtn, footerBackBtn, addRuleComponent) {
    if (addRuleComponent.wizardStep > this.getWizardMaxStep(addRuleComponent.rule) || footerNextBtn.showSpinner) {
      return;
    }

    footerNextBtn.showSpinner = true;
    const isInputCorrect = await this.checkStepInput(addRuleComponent);
    footerNextBtn.showSpinner = false;
    footerNextBtn.disabled = false;

    if (!isInputCorrect) {
      return;
    }

    if (addRuleComponent.wizardStep === this.getWizardMaxStep(addRuleComponent.rule)) {
      const frontendRule = addRuleComponent.rule,
        backendRule = convertRuleFromFrontendToBackendFormat(
          frontendRule,
          this.dependencies.gridType,
          this.dependencies.platformHasFeature(Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTIONS),
        );

      footerNextBtn.showSpinner = true;
      if (frontendRule._id) {
        if (JSON.stringify(frontendRule) === JSON.stringify(addRuleComponent.oldRule)) {
          this.addRuleDialog.dismiss();
          this.view.dom.$sideMenu.closeSideMenu();
        } else {
          const dialogTitle = translateFn('Update this rule?');
          const dialogBody = document.createElement('div');
          dialogBody.innerHTML = translateFn(
            'GravityZone will start generating incidents with new alerts, based on the updated criteria.\n' +
            'Incidents based on the initial criteria will still be visible in the Incidents page for another data retention cycle.',
          );

          const dialogFooter = document.createElement('div');
          const updateButton = new GzButtonWebComponent();
          const cancelButton = new GzButtonWebComponent();
          updateButton.innerText = translateFn('Update rule');
          updateButton.id = 'update-button';
          cancelButton.innerText = translateFn('Cancel');
          cancelButton.setAttribute('type', 'outline');
          cancelButton.setAttribute('color', 'secondary');
          cancelButton.id = 'cancel-button';
          dialogFooter.appendChild(updateButton);
          dialogFooter.appendChild(cancelButton);

          const dialogConfigs: GzDialog = {
            headline: dialogTitle,
            bodyContent: dialogBody,
            footerContent: dialogFooter,
            isOpen: true,
          };
          footerNextBtn.showSpinner = false;
          const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);
          updateButton.addEventListener('click', async () => {
            updateButton.showSpinner = true;
            try {
              await this.dependencies.api.clients.CustomRules.updateRecords([backendRule]).toPromise();
              this.addRuleDialog.dismiss();
              this.view.dom.$sideMenu.closeSideMenu();
              this.generateToastSuccess();
              await this.view.dom.$grid.controller.handleGridItemsLoad(true, true);
            } catch (error) {
              this.generateToastError(error);
            }
            dialog.dismiss();
          });

          cancelButton.addEventListener('click', () => {
            dialog.dismiss();
          });
        }
      } else {
        try {
          await this.dependencies.api.clients.CustomRules.createRecords([backendRule]).toPromise();
          this.addRuleDialog.dismiss();
          this.generateToastSuccess();
          await this.view.dom.$grid.controller.handleGridItemsLoad(true, true);
        } catch (error) {
          this.addRuleDialog.dismiss();
          if (
            error.exceptionType === 'DAL\\Exceptions\\ValidationException' &&
            error.message === 'Validation exception: YARA rules limit reached'
          ) {
            addRuleComponent.createLimitReachedDialog();
            return;
          }
          this.generateToastError();
        } finally {
          footerNextBtn.showSpinner = false;
        }
      }

      return;
    }

    addRuleComponent.wizardStep = addRuleComponent.wizardStep + 1;
    footerBackBtn.disabled = false;
    if (addRuleComponent.wizardStep === this.getWizardMaxStep(addRuleComponent.rule)) {
      footerNextBtn.innerText = translateFn('Save#preserve state');
      footerNextBtn.type = ButtonTypes.FULL;

      const ruleType = checkRuleCriteriaTypes(addRuleComponent.rule, addRuleComponent.gridType);
      footerNextBtn.disabled = (addRuleComponent.rule.targets.type === undefined) || (ruleType === Type.XDR && !addRuleComponent.hasAccessOnCompany) ||
                               (ruleType === Type.XDR && addRuleComponent.rule.targets.endpointTags);
    }
  }

  private addRuleClickOnBack(footerNextBtn, footerBackBtn, addRuleComponent) {
    footerNextBtn.showSpinner = false;
    footerNextBtn.disabled = false;
    if (addRuleComponent.wizardStep <= WizardMinStep) {
      return;
    }

    addRuleComponent.wizardStep = addRuleComponent.wizardStep - 1;

    if (addRuleComponent.wizardStep === WizardMinStep) {
      footerBackBtn.disabled = true;
    }

    if (addRuleComponent.wizardStep !== this.getWizardMaxStep(addRuleComponent.rule)) {
      footerNextBtn.innerText = translateFn('Next');
      footerNextBtn.type = ButtonTypes.OUTLINE;
    }
  }

  private generateToastSuccess() {
    GzToastNotificationWebComponent.closeAllToasts();
    const toastConfig: GzToast = {
      message: translateFn('Rule saved.'),
      severity: ToastSeverity.SUCCESS,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
      autoCloseDelayMs: 5000,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }

  private generateToastError(error: any = null) {
    GzToastNotificationWebComponent.closeAllToasts();
    const message =
      error &&
      error.exceptionType === 'DAL\\Exceptions\\SecurityException' &&
      error.message === 'Security exception: Feature not available'
      ? translateFn('Failed to save rule. EDR license missing.')
      : translateFn('Failed to save rule.');
    const toastConfig: GzToast = {
      message: message,
      severity: ToastSeverity.ERROR,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
      autoCloseDelayMs: 5000,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }

  private async addRuleListener() {
    this.view.dom.$addRuleButton.showSpinner = true;
    this.view.dom.$addRuleButton.disabled = true;
    this.view.dom.$addEmptyStateButton.showSpinner = true;
    this.view.dom.$addEmptyStateButton.disabled = true;

    await this.generateAddRule();
  }

  private async checkStepInput(addRuleComponent) {
    switch (addRuleComponent.wizardStep) {
      case 1:
        if (addRuleComponent.rule.type === RuleType.YARA) {
          if (addRuleComponent.ruleDefinition.valid) {
            return true;
          }
          await (addRuleComponent.ruleDefinition as GzYaraDefinitionWebComponent).handleValidation();
          return false;
        }

        return (addRuleComponent.ruleDefinition as GzRuleDefinitionWebComponent).handleValidation();
      case 2:
        const ruleSettings = addRuleComponent.ruleSettings;
        const ruleConfiguration = ruleSettings.ruleConfiguration as GzRuleConfigurationWebComponent;
        return await ruleConfiguration.handleValidation();
      case 3:
      case 4:
      default:
        return true;
    }
  }

  private getEditableRules() {
    const rules: any = [];

    for (const rule of this.selectedItems) {
      if (rule.canEdit) {
        rules.push(rule);
      }
    }
    return rules;
  }

  private changeStatusListener(event: WebComponentEvent) {
    const rules = this.getEditableRules();

    if (event.detail.selected === Actions.DISABLE) {
      this.view.changeStatus(Status.DISABLED, rules);
    } else if (event.detail.selected === Actions.ENABLE) {
      this.view.changeStatus(Status.ENABLED, rules);
    }
  }

  private deleteButtonListener() {
    const rules = this.getEditableRules();
    this.view.deleteRule(rules);
  }

  /**
   * Disable all actions on rules
   *
   * @private
   */
  private disableAllActions() {
    this.view.changeStatusEnable.disabled = true;
    this.view.changeStatusDisable.disabled = true;
    this.view.dom.$deleteButton.disabled = true;
  }

  /**
   * Evaluate the actions that can be performed on the selected rule
   *
   * @private
   */
  private evaluateActions() {
    let canEnable = false;
    let canDisable = false;
    let canDelete = false;

    this.selectedItems.forEach(rule => {
      if (rule['status'] === Status.ENABLED) {
        canDisable = true;
      }
      if (rule['status'] === Status.DISABLED) {
        canEnable = true;
      }
      canDelete = true;
    });

    return {
      canEnable,
      canDisable,
      canDelete,
    };
  }

  private checkEditableRules(): boolean {
    for (const rule of this.selectedItems) {
      if (!rule.canEdit) {
        return false;
      }
    }
    return true;
  }

  private onSelectionChange(event: WebComponentEvent) {
    this.selectedItems = event.detail.selectedItemsData;
    this.view.dom.$changeStatusSelect.disabled = !(
      event.detail.selectionStoreData && event.detail.selectionStoreData.length > 0
    );
    if (!Application.Main.hasAllRights([Rights.RIGHT_MANAGE_INVENTORY, Rights.MANAGE_POLICIES_WRITE])) {
      this.disableAllActions();
      return;
    }

    if (!this.checkEditableRules()) {
      return;
    }
    const { canEnable, canDisable, canDelete } = this.evaluateActions();
    this.view.changeStatusEnable.disabled = !canEnable;
    this.view.changeStatusDisable.disabled = !canDisable;
    this.view.dom.$deleteButton.disabled = !canDelete;
  }

  private async getNetworkTargets(rule) {
    if (!rule.targets ||
      (rule.targets.endpointTags && rule.targets.endpointTags.length === 0) ||
      (rule.targets.companiesIds && rule.targets.companiesIds.length === 0)) {
      return;
    }

    const targets: string[] = [];

    if (rule.targets.companyId) {
      const company = rule.availableCompanies.find(item => item._id === rule.targets.companyId);
      return company ? [company.name] : [];
    }

    if (rule.targets.companiesIds) {
      return rule.targets.companiesIds.map(companyId => {
        const company = rule.availableCompanies.find(item => item._id === companyId);
        return company ? company.name : '';
      });
    }

    const response = await this.dependencies.api.clients.CustomRules.getTagsById(
      rule.targets.endpointTags.map(tag => tag.tagId),
    ).toPromise();

    for (const entry of response.data) {
      const targetName = this.dependencies.isPartnerCompany()
                         ? entry['company']['name'] + ' / ' + entry['name']
                         : entry['name'];
      targets.push(targetName);
    }

    return targets;
  }

  private async onRowClick(event: WebComponentEvent) {
    await this.generateSidePanel(event.detail.rowDetails, event.detail.rowIndex, event.detail.maxRowIndex);
  }

  private scrollUp(index) {
    if (index < 1) {
      this.view.dom.$grid.controller.view.scrollToIndex(0);
    }
    this.view.dom.$grid.controller.view
        .getGridRowByIndex(index + 1)
        .scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
  }

  private scrollDown(index) {
    this.view.dom.$grid.controller.view
        .getGridRowByIndex(index + 2)
        .scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
  }

  private async generateSidePanel(rule, rowIndex, maxRowIndex) {
    if (!rule) {
      return;
    }

    const sidePanel = new CustomRulesSidePanelWebComponent();
    sidePanel.rule = rule;
    sidePanel.availableCompanies = rule.availableCompanies;
    sidePanel.rowIndex = rowIndex;
    sidePanel.maxRowIndex = maxRowIndex;
    sidePanel.gridType = this.dependencies.gridType;
    sidePanel.targets = await this.getNetworkTargets(rule);
    sidePanel.isGZ = this.dependencies.isGZ();
    sidePanel.isPartner = this.dependencies.isPartnerCompany();
    sidePanel.company = this.dependencies.getUserCompany();

    this.view.dom.$sideMenu.setMenuContent(sidePanel);
    this.view.dom.$sideMenu.openSideMenu();
  }

  protected async handleStartYaraScan(event) {
    await this.sidePanelScan(event);
  }

  public async startScan(rule: any, paths: any) {
    const startScanButton = this.startScanDialog.shadowRoot?.querySelector('#startScanButton') as GzButtonWebComponent;
    if (startScanButton.showSpinner) {
      return;
    }

    this.view.dom.$grid.controller.setMoreItemsLoadingState(true);
    startScanButton.showSpinner = true;
    try {
      await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'DetectionRules',
        method: 'startScan',
        payload: {
          ruleId: rule['_id'],
          paths,
        },
      });

      const actionCallback = () => {
        this.dependencies.navigateToTasks();
        GzToastNotificationWebComponent.closeAllToasts();
      };
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn('Task created.'),
        severity: ToastSeverity.SUCCESS,
        actionButtonText: this.dependencies.translateFn('View status.'),
        hasAction: true,
        actionButtonMode: ToastActionButtonMode.LINK,
        actionCallback,
      });
    } catch (error) {
      let message = translateFn('Failed to create task.');
      if (error.err?.exceptionType === 'Exceptions\\NoTargetsFoundException') {
        message = translateFn('Failed to create task. No valid targets found.');
      }
      const toastNotificationConfigs: GzToast = {
        message,
        hasAction: false,
        severity: ToastSeverity.ERROR,
        keepOpen: false,
        autoCloseDelayMs: 5000,
      };

      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
    }

    this.view.dom.$grid.controller.setMoreItemsLoadingState(false);
    this.startScanDialog.dismiss();
    this.view.dom.$sideMenu.closeSideMenu();
  }
}
